import type { IHeritageToken } from "../VendorBody/VendorItems/VendorConnectionModal/types/IHeritageConnection";

export function isHeritageTokenActive(
  createdAt: string | undefined,
  expiresIn: number | null | undefined
): boolean {
  if (!createdAt || expiresIn == null) return false;
  const expiresAt = new Date(createdAt).getTime() + expiresIn * 1000;
  return Date.now() < expiresAt;
}

export function isHeritageCompanyConnected(connection: IHeritageToken | undefined): boolean {
  if (!connection?.credentials_exist) return false;
  return isHeritageTokenActive(connection.created_at, connection.expires_in);
}

export type HeritageConnectPhase = "idle" | "connecting" | "failed";

export type HeritageConnectionState =
  | "connected"
  | "credentials_missing"
  | "token_expired"
  | "token_missing";

export type HeritageAuthStatusSnapshot = {
  connected: boolean;
  hasCredentials: boolean;
  tokenExpired: boolean;
  tokenMissing?: boolean;
  connectionState?: HeritageConnectionState;
};

export type HeritageConnectionContext = {
  connectPhase?: HeritageConnectPhase;
  authStatus?: HeritageAuthStatusSnapshot;
};

export type HeritageVendorStatus = {
  labelKey: string;
  tooltipKey?: string;
  tone: "success" | "warning" | "info" | "error";
};

/** Resolve a stable connection state from auth status (API or inferred). */
export function resolveHeritageConnectionState(
  auth?: HeritageAuthStatusSnapshot | null
): HeritageConnectionState | null {
  if (!auth) return null;
  if (auth.connectionState) return auth.connectionState;
  if (!auth.hasCredentials) return "credentials_missing";
  if (auth.connected) return "connected";
  if (auth.tokenExpired) return "token_expired";
  if (auth.tokenMissing) return "token_missing";
  return "token_missing";
}

function resolveConnectionFailure(ctx?: HeritageConnectionContext): { tooltipKey: string } | null {
  if (!ctx) return null;

  if (ctx.connectPhase === "failed") {
    return {
      tooltipKey:
        "Heritage connection failed. Check credentials in Edit Vendor, then Connect again. If the problem continues, notify support.",
    };
  }

  const state = resolveHeritageConnectionState(ctx.authStatus);
  if (state === "credentials_missing") {
    return {
      tooltipKey: "Add Heritage credentials in Edit Vendor to connect your company to Heritage.",
    };
  }
  if (state === "token_expired") {
    return {
      tooltipKey:
        "Heritage login expired. Credentials are still saved — click Connect Company to Heritage to refresh the token.",
    };
  }
  if (state === "token_missing") {
    return {
      tooltipKey:
        "Heritage credentials are saved but the company is not connected. Click Connect Company to Heritage.",
    };
  }

  return null;
}

/** Single Heritage badge: company connection + vendor catalog verification. */
export function getHeritageVendorStatus(
  isConnected: boolean,
  isVerified: boolean,
  ctx?: HeritageConnectionContext
): HeritageVendorStatus {
  const connectionState = resolveHeritageConnectionState(ctx?.authStatus);

  if (connectionState === "credentials_missing") {
    return {
      labelKey: "Heritage Credentials Missing",
      tooltipKey:
        "Add Heritage credentials in Edit Vendor to connect your company to Heritage.",
      tone: "warning",
    };
  }

  if (isConnected && isVerified) {
    return { labelKey: "Heritage Verified", tone: "success" };
  }
  if (isConnected) {
    return {
      labelKey: "Heritage Connected",
      tooltipKey:
        "Heritage is connected. Download branch catalog in Edit Vendor to load items for this vendor.",
      tone: "info",
    };
  }

  if (ctx?.connectPhase === "connecting") {
    return {
      labelKey: "Heritage Connecting...",
      tooltipKey: "Attempting to connect your company to Heritage.",
      tone: "info",
    };
  }

  if (connectionState === "token_expired") {
    return {
      labelKey: "Heritage Token Expired",
      tooltipKey:
        "Heritage login expired. Credentials are still saved — open Edit Vendor and Connect to refresh the token. Catalog data is kept.",
      tone: "warning",
    };
  }

  if (connectionState === "token_missing") {
    return {
      labelKey: "Heritage Reconnect Required",
      tooltipKey:
        "Heritage credentials are saved but the company is not connected. Open Edit Vendor and Connect. Catalog data is kept.",
      tone: "warning",
    };
  }

  const failure = resolveConnectionFailure(ctx);
  if (failure) {
    return {
      labelKey: "Heritage Connection Failed",
      tone: "error",
      ...failure,
    };
  }

  if (isVerified) {
    return {
      labelKey: "Heritage Reconnect Required",
      tooltipKey:
        "Catalog data exists but the company Heritage connection has expired. Reconnect by opening this vendor — credentials and catalog are kept.",
      tone: "warning",
    };
  }

  return {
    labelKey: "Heritage Not Connected",
    tooltipKey:
      "Heritage connection pending. Connection is attempted automatically when you open a Heritage vendor.",
    tone: "warning",
  };
}

const HERITAGE_BADGE_CLASS: Record<HeritageVendorStatus["tone"], string> = {
  success: "text-green-800 bg-green-100",
  warning: "text-yellow-700 bg-yellow-200",
  info: "text-blue-800 bg-blue-100",
  error: "text-red-800 bg-red-100",
};

export function heritageVendorStatusBadgeClass(tone: HeritageVendorStatus["tone"]): string {
  return HERITAGE_BADGE_CLASS[tone];
}
